/**
 * Dashboard API
 *
 * Functions for fetching dashboard statistics and data.
 */

import { api } from './client';

// =============================================================================
// Types
// =============================================================================

export interface DashboardStats {
  upcomingEvents: number;
  pendingAssignments: number;
  /**
   * The active CREW roster: owners, admins, archived and deactivated members
   * are all excluded. This is the denominator for `crewResponded`.
   */
  activeCrew: number;
  /** Total role slots on crew-visible events in the next 7 days. */
  roleSlots: number;
  /** Of those slots, how many nobody holds. Never negative. */
  rolesUnfilled: number;
  /** Active crew who set availability on at least one of those events. */
  crewResponded: number;
}

export interface UpcomingEvent {
  id: string;
  name: string;
  venue: string;
  eventDate: string;
  callTime?: string | null;
  startTime?: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'COMPLETED' | 'CANCELLED';
  crew: {
    assigned: number;
    confirmed: number;
    pending: number;
    needed: number;
    /** Active crew who answered. null when the event is not crew-visible. */
    responded: number | null;
  };
}

export interface PendingAssignment {
  id: string;
  assignedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
  event: {
    id: string;
    name: string;
    eventDate: string;
  };
  role: {
    id: string;
    roleName: string;
  };
}

export interface DashboardData {
  stats: DashboardStats;
  upcomingEvents: UpcomingEvent[];
  pendingAssignments: PendingAssignment[];
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get dashboard statistics and upcoming events
 */
export async function getDashboardData(): Promise<DashboardData> {
  const response = await api.get<DashboardData>('/dashboard/stats');
  return response.data;
}
